import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeOverlappingModal } from '../../../../actions';
import { Button } from '@tempmee/react';
import { Row } from '../../../../commonComponents';
import WarningIcon from '../../../../images/icons/Warning';
import { Colors } from '../../../../themes/colors';
import ConfirmPopup from '../../../shared/ConfirmPopup';
import { useConfirmStep } from '../hooks/useConfirmStep';
import { GuideLinesSwitch } from './Guidelines/GuidelinesSwitch';
import { OverlappingJobPopup } from '~/commonComponents/popups/OverlappingJobPopup';

export const ConfirmRequest = () => {
  const dispatch = useDispatch();
  const [acceptedGuidelines, setAcceptedGuidelines] = useState(false);
  const {
    handleConfirmButton,
    job,
    showLeavingPopup,
    showJobCreatePaymentError,
    setShowLeavingPopup,
    confirmOverlapping,
    cancelRequest,
    hidePaymentError,
    updatePayment,
    setShowOvertimeLimitPopup,
    showOvertimeLimitPopup,
  } = useConfirmStep();

  return (
    <div
      data-testid="confirm-request-section"
      data-cy="confirm-request-section"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 120,
        marginTop: 60,
      }}
    >
      <GuideLinesSwitch onSwitch={setAcceptedGuidelines} />

      <Row gap={16}>
        <Button
          data-cy="cancel-request-button"
          variant="secondary"
          size="lg"
          onClick={() => setShowLeavingPopup(true)}
        >
          Cancel Request
        </Button>
        <Button
          data-cy="send-request-button"
          size="lg"
          onClick={handleConfirmButton}
          disabled={!acceptedGuidelines}
        >
          Send Request
        </Button>
      </Row>

      {showJobCreatePaymentError && (
        <ConfirmPopup
          dataCy="payment-error-popup"
          dataTestId="payment-error-popup"
          leftButtonDataCy="payment-error-go-back"
          rightButtonDataCy="payment-error-update"
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon />
            </div>
          )}
          title="Auto-Confirm Disabled"
          description={
            <>
              The auto-confirm feature is only available for offices that have input their payment
              information.
              <br />
              <br />
              You can update your payment information now or continue without payment, and with
              auto-confirm disabled.
            </>
          }
          descriptionStyle={{ marginBottom: 45 }}
          containerStyle={{ paddingTop: 45, paddingBottom: 50 }}
          leftButtonText="Go Back"
          leftButtonStyle={{
            backgroundColor: Colors.secondary_500,
          }}
          leftButtonAction={hidePaymentError}
          rightButtonText="Update"
          rightButtonAction={updatePayment}
        />
      )}

      {showLeavingPopup && (
        <ConfirmPopup
          dataCy="cancel-confirmation-popup"
          dataTestId="cancel-confirmation-popup"
          leftButtonDataCy="cancel-confirmation-go-back"
          rightButtonDataCy="cancel-confirmation-confirm"
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} />
            </div>
          )}
          title="Please Confirm"
          description="Are you sure you would like to cancel this request?"
          descriptionStyle={{ marginBottom: 100 }}
          containerStyle={{ paddingTop: 45, paddingBottom: 50 }}
          leftButtonText="Go Back"
          closePopup={() => setShowLeavingPopup(false)}
          leftButtonStyle={{
            backgroundColor: Colors.secondary_500,
          }}
          leftButtonAction={() => setShowLeavingPopup(false)}
          rightButtonText="Confirm"
          rightButtonAction={cancelRequest}
        />
      )}

      <OverlappingJobPopup
        isOpen={job?.isOverlappingJob}
        onConfirm={confirmOverlapping}
        onGoBack={() => dispatch(closeOverlappingModal())}
      />

      {showOvertimeLimitPopup && (
        <ConfirmPopup
          dataCy="overtime-limit-popup"
          dataTestId="overtime-limit-popup"
          leftButtonDataCy="overtime-limit-go-back"
          rightButtonDataCy="overtime-limit-continue"
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} color={Colors.warning_500} />
            </div>
          )}
          title="Overtime Hours"
          description={`
            The payable hours of this shift exceed your state’s daily 
            overtime limit. Your office will be subject to additional
            charges for any overtime hours worked by a professional.
          `}
          descriptionStyle={{ width: '76%', marginBottom: 50 }}
          leftButtonText="Go Back"
          leftButtonAction={() => setShowOvertimeLimitPopup(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            handleConfirmButton({ alertOvertime: false });
            setShowOvertimeLimitPopup(false);
          }}
        />
      )}
    </div>
  );
};
